import type { ReactNode } from "react";
import { AppWindow } from "lucide-react";
import { Message } from "@/i18n/Message";

/**
 * The Default Splash for a launching app surface: app icon, app name (when it
 * is known before the launch starts), and one fixed status line. It owns the
 * whole surface until the app is operable, and its DOM never changes while the
 * launch progresses — internal phases (allocation, iframe first paint, load)
 * must not swap this component or edit its copy.
 *
 * `name` is optional on purpose: an unresolved title must not render a
 * placeholder that later becomes the real name. The caller freezes the name for
 * the launch, so an unknown title simply stays unknown for that launch.
 *
 * A custom Splash can replace the body later (`custom`) without changing the
 * surface around it; no catalogue/API field is needed for that yet.
 */
export default function AppSplash({
  name,
  icon,
  inline = false,
  custom,
}: {
  name?: string | null;
  icon?: ReactNode;
  /** Inside a stage that already sizes the surface (standalone launch pages):
   *  no absolute overlay positioning. */
  inline?: boolean;
  /** Future Custom Splash slot. The default body is used when absent. */
  custom?: ReactNode;
}) {
  const label = name?.trim();
  return (
    <div
      className={`app-splash${inline ? " app-splash--inline" : ""}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {custom ?? (
        <div className="app-splash-body">
          <span className="app-splash-icon" aria-hidden="true">
            {icon ?? (label
              ? <span className="app-splash-initial">{label.charAt(0).toUpperCase()}</span>
              : <AppWindow size={26} />)}
          </span>
          {label ? <p className="app-splash-name" title={label}>{label}</p> : null}
          <p className="app-splash-status"><Message message="Preparing your app…" /></p>
        </div>
      )}
    </div>
  );
}
